import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, ChevronRight, ChevronDown, Edit2, Trash2, Users, UserPlus, UserMinus, Search, ArrowRightLeft } from "lucide-react";

/**
 * Admin → Org Units. Flat list from `/org/units`, rendered as an indented
 * tree client-side. Each node can be edited inline (name, kind, head,
 * parent) and children auto-reparent on delete. Cycle protection is
 * enforced by the parent-picker disabling self + descendants.
 */
const KIND_LABELS = { company: "Company", division: "Division", team: "Team", squad: "Squad" };

export default function AdminOrgUnits() {
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [rollups, setRollups] = useState({}); // unit_id → members[]
  const [editing, setEditing] = useState(null); // {id?, name, parent_unit_id, head_user_id, kind, description}
  const [addingMember, setAddingMember] = useState({}); // unit_id -> { search, uid, unit_role }
  const [handover, setHandover] = useState(null); // {unit_id, unit_name, current_head_id, new_head_id, also_reassign}

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/org/units"),
      api.get("/admin/users?role=&q=&page=1&page_size=500"),
    ]).then(([u, us]) => {
      const unitsData = u.data || [];
      setUnits(unitsData);
      setUsers((us.data?.items || us.data || []).filter((x) => x.role !== "client"));
      // Default: expand every unit so heads & members are visible without clicks.
      setExpanded(new Set(unitsData.map((x) => x.id)));
      // Also prefetch member rollups for every unit so the tree is ready-to-scan.
      unitsData.forEach((x) => {
        api.get(`/org/units/${x.id}/rollup`)
          .then((r) => setRollups((m) => ({ ...m, [x.id]: r.data.members || [] })))
          .catch(() => {});
      });
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const byParent = useMemo(() => {
    const m = new Map();
    for (const u of units) {
      const k = u.parent_unit_id || "__root__";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(u);
    }
    return m;
  }, [units]);
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.user_id, u])), [users]);

  const descendantIds = (id) => {
    const out = new Set([id]);
    const q = [id];
    while (q.length) {
      const cur = q.shift();
      for (const c of (byParent.get(cur) || [])) { out.add(c.id); q.push(c.id); }
    }
    return out;
  };

  const toggle = async (id) => {
    setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    if (!rollups[id]) {
      try {
        const r = await api.get(`/org/units/${id}/rollup`);
        setRollups((m) => ({ ...m, [id]: r.data.members || [] }));
      } catch { /* silent */ }
    }
  };

  const save = async () => {
    if (!editing?.name?.trim()) { toast.error("Name required"); return; }
    try {
      const payload = {
        name: editing.name.trim(),
        parent_unit_id: editing.parent_unit_id || null,
        head_user_id: editing.head_user_id || null,
        kind: editing.kind || "team",
        description: editing.description || "",
      };
      if (editing.id) await api.put(`/admin/org/units/${editing.id}`, payload);
      else await api.post("/admin/org/units", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete unit ${u.name}?\n\nChildren and members auto-reparent to the parent unit.`)) return;
    try { await api.delete(`/admin/org/units/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const refreshRollup = async (unitId) => {
    try {
      const r = await api.get(`/org/units/${unitId}/rollup`);
      setRollups((m) => ({ ...m, [unitId]: r.data.members || [] }));
    } catch { /* silent */ }
  };
  const addMember = async (unitId) => {
    const state = addingMember[unitId] || {};
    if (!state.uid) { toast.error("Pick a user first"); return; }
    try {
      await api.post(`/admin/org/units/${unitId}/members`, {
        user_id: state.uid,
        unit_role: (state.unit_role || "").trim() || null,
      });
      toast.success("Member added");
      setAddingMember((s) => ({ ...s, [unitId]: { search: "", uid: "", unit_role: "" } }));
      await refreshRollup(unitId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const removeMember = async (unitId, uid, name) => {
    if (!window.confirm(`Remove ${name || uid} from this unit?`)) return;
    try {
      await api.delete(`/admin/org/units/${unitId}/members/${uid}`);
      toast.success("Removed");
      await refreshRollup(unitId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const doHandover = async () => {
    if (!handover?.new_head_id) { toast.error("Pick the incoming head"); return; }
    try {
      const r = await api.post(`/admin/org/units/${handover.unit_id}/handover`, {
        new_head_user_id: handover.new_head_id,
        also_reassign_direct_reports: handover.also_reassign !== false,
      });
      toast.success(`Handover complete · ${r.data.reports_reassigned || 0} report(s) transferred`);
      setHandover(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Handover failed"); }
  };

  const renderNode = (u, depth = 0) => {
    const kids = byParent.get(u.id) || [];
    const open = expanded.has(u.id);
    const head = u.head_user_id ? usersById[u.head_user_id] : null;
    return (
      <React.Fragment key={u.id}>
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 hover:bg-slate-50" style={{ paddingLeft: 12 + depth * 20 }} data-testid={`org-unit-${u.id}`}>
          <button type="button" onClick={() => toggle(u.id)} className="p-0.5 text-kb-ink-muted">
            {kids.length > 0 || (rollups[u.id] || []).length > 0 ? (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : <span className="inline-block w-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-kb-ink truncate flex items-center gap-2 flex-wrap">
              {u.name}
              <span className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-kb-ink-muted">{KIND_LABELS[u.kind] || u.kind}</span>
              {head ? (
                <span className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1">
                  ★ Head: {head.name}
                </span>
              ) : (
                <span className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-light">No head assigned</span>
              )}
              <span className="kb-mono text-[9px] text-kb-ink-light">· {(rollups[u.id] || []).length} member{(rollups[u.id] || []).length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <button type="button" onClick={() => setHandover({ unit_id: u.id, unit_name: u.name, current_head_id: u.head_user_id || "", new_head_id: "", also_reassign: true })} className="p-1 text-kb-ink-muted hover:text-kb-primary" title="Handover head" data-testid={`org-handover-${u.id}`}><ArrowRightLeft className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={() => setEditing({ ...u })} className="p-1 text-kb-ink-muted hover:text-kb-ink" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={() => remove(u)} className="p-1 text-rose-500 hover:text-rose-700" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        {open && (
          <div className="bg-slate-50 border-b border-slate-100" style={{ paddingLeft: 32 + depth * 20 }}>
            {(rollups[u.id] || []).map((m) => (
              <div key={m.user_id} className="flex items-center gap-2 py-1 text-xs" data-testid={`org-member-${u.id}-${m.user_id}`}>
                <Users className="w-3 h-3 text-kb-ink-light" />
                <span className="flex-1 truncate">{m.name || m.email}{m.unit_role ? <em className="text-kb-ink-muted not-italic"> — {m.unit_role}</em> : null}</span>
                <span className="kb-mono text-[9px] text-kb-ink-light">{m.role}{m.employee_role ? ` · ${m.employee_role}` : ""}</span>
                {(m.secondary_roles || []).length > 0 && (
                  <span className="kb-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-indigo-50 border border-indigo-200 text-indigo-700" title="Additional roles">+{(m.secondary_roles || []).filter((r) => r !== m.role).join(", ") || "—"}</span>
                )}
                <span className="kb-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-white border border-slate-200 text-kb-ink-muted">{m.tasks?.open || 0} open</span>
                {m.tasks?.overdue > 0 && <span className="kb-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-rose-50 border border-rose-200 text-rose-700">{m.tasks.overdue} overdue</span>}
                {/* Only show remove if the user is directly attached to THIS unit (not a descendant). */}
                {m.unit_id === u.id && (
                  <button type="button" onClick={() => removeMember(u.id, m.user_id, m.name)} className="p-0.5 text-rose-500 hover:text-rose-700" title="Remove from unit" data-testid={`org-member-remove-${u.id}-${m.user_id}`}>
                    <UserMinus className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {/* Add member row */}
            <div className="flex flex-wrap items-center gap-1.5 py-2 pr-3" data-testid={`org-add-member-${u.id}`}>
              <div className="relative flex-1 min-w-[160px]">
                <Search className="w-3 h-3 text-kb-ink-light absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  list={`org-users-${u.id}`}
                  placeholder="Search & pick user…"
                  className="kb-input pl-6 text-xs h-7"
                  value={addingMember[u.id]?.search || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    const match = users.find((x) => `${x.name || x.email} — ${x.email}` === val || x.email === val);
                    setAddingMember((s) => ({ ...s, [u.id]: { ...(s[u.id] || {}), search: val, uid: match?.user_id || "" } }));
                  }}
                  data-testid={`org-add-member-input-${u.id}`}
                />
                <datalist id={`org-users-${u.id}`}>
                  {users.slice(0, 200).map((x) => <option key={x.user_id} value={`${x.name || x.email} — ${x.email}`} />)}
                </datalist>
              </div>
              <input
                placeholder="In-unit role (e.g. Lead)"
                className="kb-input text-xs h-7 w-40"
                value={addingMember[u.id]?.unit_role || ""}
                onChange={(e) => setAddingMember((s) => ({ ...s, [u.id]: { ...(s[u.id] || {}), unit_role: e.target.value } }))}
                data-testid={`org-add-member-role-${u.id}`}
              />
              <button type="button" onClick={() => addMember(u.id)} disabled={!addingMember[u.id]?.uid} className="kb-btn-primary text-[10px] h-7 px-2 disabled:opacity-40" data-testid={`org-add-member-btn-${u.id}`}>
                <UserPlus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
        )}
        {open && kids.map((c) => renderNode(c, depth + 1))}
      </React.Fragment>
    );
  };

  const roots = byParent.get("__root__") || [];
  return (
    <div data-testid="admin-org-units-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADMIN · ORG</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Org <em className="italic text-kb-primary">units</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Your <strong>permanent</strong> reporting structure. A unit&apos;s Head automatically sees every member of the subtree on their <strong>Direct Reports</strong> page — no separate supervisor assignment needed. Ad-hoc squads live in <Link to="/admin/teams" className="text-kb-primary underline">Project Teams</Link>.</p>
        </div>
        <button onClick={() => setEditing({ name: "", parent_unit_id: null, head_user_id: null, kind: "team", description: "" })} className="kb-btn-primary text-sm" data-testid="admin-org-new-btn">
          <Plus className="w-4 h-4" /> New unit
        </button>
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        units.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No units yet. Create the top-level Company or Division first.</div>
        ) : (
          <div className="mt-6 border border-slate-200 rounded-sm bg-white overflow-hidden">
            {roots.map((r) => renderNode(r, 0))}
          </div>
        )}

      {editing && (
        <div className="fixed inset-0 z-[160] bg-black/40 flex items-center justify-center p-4" data-testid="admin-org-modal">
          <div className="bg-white rounded-sm shadow-2xl max-w-md w-full border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-serif text-lg text-kb-ink">{editing.id ? "Edit unit" : "New unit"}</h3>
              <button onClick={() => setEditing(null)} className="p-1 text-kb-ink-muted hover:text-kb-ink"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Name *</label>
                <input className="kb-input mt-1" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-org-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Kind</label>
                  <select className="kb-input mt-1" value={editing.kind || "team"} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                    {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Parent unit</label>
                  <select className="kb-input mt-1" value={editing.parent_unit_id || ""} onChange={(e) => setEditing({ ...editing, parent_unit_id: e.target.value || null })}>
                    <option value="">— root —</option>
                    {units.filter((u) => !editing.id || !descendantIds(editing.id).has(u.id)).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Head / lead</label>
                <select className="kb-input mt-1" value={editing.head_user_id || ""} onChange={(e) => setEditing({ ...editing, head_user_id: e.target.value || null })} data-testid="admin-org-head">
                  <option value="">— none —</option>
                  {users.filter((u) => ["employee", "admin", "super_admin", "mentor"].includes(u.role)).map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Description</label>
                <textarea rows={2} className="kb-input mt-1 resize-none" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="kb-btn-outline text-xs">Cancel</button>
              <button onClick={save} className="kb-btn-primary text-xs" data-testid="admin-org-save">{editing.id ? "Save" : "Create"}</button>
            </div>
          </div>
        </div>
      )}
      {handover && (
        <div className="fixed inset-0 z-[160] bg-black/40 flex items-center justify-center p-4" data-testid="admin-org-handover-modal">
          <div className="bg-white rounded-sm shadow-2xl max-w-md w-full border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-serif text-lg text-kb-ink">Handover head · {handover.unit_name}</h3>
              <button onClick={() => setHandover(null)} className="p-1 text-kb-ink-muted hover:text-kb-ink"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="text-xs text-kb-ink-muted">
                Current head: <strong className="text-kb-ink">{handover.current_head_id ? (usersById[handover.current_head_id]?.name || handover.current_head_id) : "— none —"}</strong>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Incoming head *</label>
                <select
                  className="kb-input mt-1"
                  value={handover.new_head_id}
                  onChange={(e) => setHandover((h) => ({ ...h, new_head_id: e.target.value }))}
                  data-testid="admin-org-handover-new-head"
                >
                  <option value="">— pick a user —</option>
                  {users
                    .filter((u) => ["employee", "admin", "super_admin", "mentor"].includes(u.role) && u.user_id !== handover.current_head_id)
                    .map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}{u.employee_role ? ` · ${u.employee_role}` : ""}</option>)}
                </select>
              </div>
              <label className="flex items-start gap-2 text-xs text-kb-ink-muted select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={handover.also_reassign !== false}
                  onChange={(e) => setHandover((h) => ({ ...h, also_reassign: e.target.checked }))}
                  className="w-4 h-4 accent-kb-primary mt-0.5"
                  data-testid="admin-org-handover-reassign"
                />
                <span>Also move everyone whose supervisor was the outgoing head over to the new one — keeps the reporting line intact.</span>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setHandover(null)} className="kb-btn-outline text-xs">Cancel</button>
              <button onClick={doHandover} className="kb-btn-primary text-xs" data-testid="admin-org-handover-confirm">
                <ArrowRightLeft className="w-3.5 h-3.5" /> Handover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
